import React from 'react';
import { Table } from 'antd';
import { type ColumnsType, type TablePaginationConfig, type TableProps } from 'antd/es/table';
import { type SortOrder } from 'antd/es/table/interface';
import numeral from 'numeral';
import dayjs from 'dayjs';
import { type PredictionsFilters, type PredictionsTableRow } from '../../../../types/predictions';
import { TruncatedCell } from '../../finances-v2/components/TruncatedCell';

type OnTableChange = TableProps<PredictionsTableRow>['onChange'];

interface PredictionsTableProps {
  data: PredictionsTableRow[];
  loading: boolean;
  filters: PredictionsFilters;
  pagination: false | TablePaginationConfig;
  onTableChange?: OnTableChange;
  sortState: {
    by?: string;
    direction?: 'ASC' | 'DESC';
  };
}

export const PredictionsTable: React.FC<PredictionsTableProps> = ({
  data,
  loading,
  filters,
  pagination,
  onTableChange,
  sortState,
}) => {
  const toAntSortOrder = (direction?: 'ASC' | 'DESC'): SortOrder | undefined => {
    if (!direction) return undefined;
    return direction === 'ASC' ? 'ascend' : 'descend';
  };

  const getSortOrderForColumn = (columnKey: string): SortOrder | undefined => {
    if (sortState.by !== columnKey) return undefined;
    return toAntSortOrder(sortState.direction);
  };

  const columns: ColumnsType<PredictionsTableRow> = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      width: 60,
      align: 'right',
      render: (value: number) => value ?? '-',
    },
  ];

  if (filters.format !== 'summary') {
    columns.push({
      title: 'Date',
      dataIndex: 'datetime',
      key: 'datetime',
      render: (datetime: string) => {
        if (!datetime) return '-';
        if (filters.format === 'by_hour') return dayjs(datetime).format('YYYY-MM-DD HH:mm');
        if (filters.format === 'by_day') return dayjs(datetime).format('YYYY-MM-DD');
        return datetime;
      },
      sorter: true,
      sortOrder: getSortOrderForColumn('datetime'),
      sortDirections: ['ascend', 'descend'],
    });
  }

  if (filters.groupBy?.includes('sspId')) {
    columns.push({
      title: 'SSP',
      dataIndex: 'sspId',
      key: 'sspId',
      render: (sspId: string) => <TruncatedCell text={sspId || 'Unknown'} />,
      sorter: true,
      sortOrder: getSortOrderForColumn('sspId'),
      sortDirections: ['ascend', 'descend'],
    });
  }

  if (filters.groupBy?.includes('dspId')) {
    columns.push({
      title: 'DSP',
      dataIndex: 'dspId',
      key: 'dspId',
      render: (dspId: string) => <TruncatedCell text={dspId || 'Unknown'} />,
      sorter: true,
      sortOrder: getSortOrderForColumn('dspId'),
      sortDirections: ['ascend', 'descend'],
    });
  }

  columns.push(
    {
      title: 'Total Predictions',
      dataIndex: 'totalPredictions',
      key: 'totalPredictions',
      render: (value: number) => numeral(value || 0).format('0,0'),
      align: 'right',
      sorter: true,
      sortOrder: getSortOrderForColumn('totalPredictions'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Predicted Wins',
      dataIndex: 'predictedWins',
      key: 'predictedWins',
      render: (value: number) => numeral(value || 0).format('0,0'),
      align: 'right',
      sorter: true,
      sortOrder: getSortOrderForColumn('predictedWins'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Actual Wins',
      dataIndex: 'actualWins',
      key: 'actualWins',
      render: (value: number) => numeral(value || 0).format('0,0'),
      align: 'right',
      sorter: true,
      sortOrder: getSortOrderForColumn('actualWins'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Correctly Predicted Wins',
      dataIndex: 'correctlyPredictedWins',
      key: 'correctlyPredictedWins',
      render: (value: number) => numeral(value || 0).format('0,0'),
      align: 'right',
      sorter: true,
      sortOrder: getSortOrderForColumn('correctlyPredictedWins'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Precision',
      dataIndex: 'precisionPct',
      key: 'precisionPct',
      render: (value: number) => `${numeral(value || 0).format('0.000')}%`,
      align: 'right',
      sorter: true,
      sortOrder: getSortOrderForColumn('precisionPct'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Recall',
      dataIndex: 'recallPct',
      key: 'recallPct',
      render: (value: number) => `${numeral(value || 0).format('0.000')}%`,
      align: 'right',
      sorter: true,
      sortOrder: getSortOrderForColumn('recallPct'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Avg Prediction Score',
      dataIndex: 'avgPredictionScore',
      key: 'avgPredictionScore',
      render: (value: number) => numeral(value || 0).format('0.000000'),
      align: 'right',
      sorter: true,
      sortOrder: getSortOrderForColumn('avgPredictionScore'),
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Predicted Win Rate',
      dataIndex: 'predictedWinRatePct',
      key: 'predictedWinRatePct',
      render: (value: number) => `${numeral(value || 0).format('0.000')}%`,
      align: 'right',
      sorter: true,
      sortOrder: getSortOrderForColumn('predictedWinRatePct'),
      sortDirections: ['ascend', 'descend'],
    }
  );

  const headerWrappedColumns: ColumnsType<PredictionsTableRow> = columns.map((col) => ({
    ...col,
    onHeaderCell: (column) => {
      const base = typeof col.onHeaderCell === 'function' ? col.onHeaderCell(column) : {};
      return {
        ...base,
        style: {
          ...(base as React.HTMLAttributes<HTMLElement>).style,
          whiteSpace: 'normal',
          wordBreak: 'normal',
          overflowWrap: 'break-word',
          lineHeight: 1.1,
        },
      } as React.HTMLAttributes<HTMLElement>;
    },
  }));

  return (
    <Table<PredictionsTableRow>
      size="small"
      pagination={pagination}
      dataSource={data}
      loading={loading}
      columns={headerWrappedColumns}
      rowKey="key"
      onChange={onTableChange}
      scroll={{ x: 'max-content' }}
    />
  );
};
